import { Controller } from 'react-hook-form';
import { IBirth } from '@clickersphere/core/entities/IBirth';
import { DELIVERY_METHOD_VALUES } from '@clickersphere/core/enums/DeliveryMethodEnum';
import { Button } from '../../Button';
import DateInput from '../../DateInput';
import { Input } from '../../Input';
import LocationCombobox from '../../Location/LocationCombobox';
import Select from '../../Select';
import { Textarea } from '../../Textarea';
import { deliveryMethodLabelKeys } from '../birthEnumLabels';
import { useBirthFormController } from './useBirthFormController';

interface IBirthFormProps {
  leadId: string;
  birth?: IBirth;
}

export default function BirthForm({ leadId, birth }: IBirthFormProps) {
  const {
    t,
    errors,
    control,
    register,
    isFetching,
    selectedLocationId,
    setSelectedLocationId,
    handleSubmit,
    handleFormReset,
  } = useBirthFormController({ leadId, birth });

  return (
    <form
      className="my-4 flex w-full flex-col items-center gap-4"
      onSubmit={handleSubmit}
    >
      <Controller
        control={control}
        name="expectedBirthDate"
        render={({
          field: { value, name, onChange },
          fieldState: { error },
        }) => (
          <DateInput
            name={name}
            value={value ?? ''}
            onChange={onChange}
            placeholder={t('birth.form.expectedBirthDate')}
            error={error?.message}
            disabled={isFetching}
          />
        )}
      />

      <Controller
        control={control}
        name="birthDate"
        render={({
          field: { value, name, onChange },
          fieldState: { error },
        }) => (
          <DateInput
            name={name}
            value={value ?? ''}
            onChange={onChange}
            placeholder={t('birth.form.birthDate')}
            error={error?.message}
            disabled={isFetching}
          />
        )}
      />

      <Input
        type="text"
        placeholder={t('birth.form.babyName')}
        {...register('babyName')}
        error={errors.babyName?.message}
        disabled={isFetching}
      />

      <Controller
        control={control}
        name="deliveryMethod"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Select
            placeholder={t('birth.form.deliveryMethod')}
            options={DELIVERY_METHOD_VALUES.map((method) => ({
              value: method,
              text: t(deliveryMethodLabelKeys[method]),
            }))}
            emptyOption
            emptyOptionLabel={t('birth.form.deliveryMethodNone')}
            value={value ?? ''}
            onChange={onChange}
            error={error?.message}
            disabled={isFetching}
          />
        )}
      />

      <LocationCombobox
        value={selectedLocationId}
        onChange={(locationId) => setSelectedLocationId(locationId)}
        placeholder={t('birth.form.location')}
        disabled={isFetching}
      />

      <Textarea
        placeholder={t('birth.form.photographyWishes')}
        {...register('photographyWishes')}
        error={errors.photographyWishes?.message}
        disabled={isFetching}
      />

      <section className="mt-4 flex w-full flex-col gap-3 sm:flex-row sm:gap-4">
        <Button
          className="sm:w-50"
          disabled={isFetching}
          color="ghost"
          onClick={() => handleFormReset()}
        >
          {t('birth.form.clear')}
        </Button>
        <Button type="submit" disabled={isFetching} isLoading={isFetching}>
          {t('birth.form.submit')}
        </Button>
      </section>
    </form>
  );
}
